import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useOrganizationTheme } from "./hooks/useOrganizationTheme";
import { useBackgroundEmailSync } from "./hooks/useBackgroundEmailSync";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { ReleaseNotesDialog } from "./components/ReleaseNotesDialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
// OnboardingProcessing and OnboardingProfile removed - users join via invitation only
import Archive from "./pages/Archive";
import ContentEditor from "./pages/ContentEditor";
import Subscription from "./pages/Subscription";
import Welcome from "./pages/Welcome";
import Performance from "./pages/Performance";
import CustomerManagement from "./pages/CustomerManagement";
import Profile from "./pages/Profile";
import OrganizationManagement from "./pages/OrganizationManagement";
import Email from "./pages/Email";
import RunningList from "./pages/RunningList";
import TodoManagement from "./pages/TodoManagement";
import CustomerConfirmation from "./pages/CustomerConfirmation";
import CustomerAppointment from "./pages/CustomerAppointment";
import EmailConfirmation from "./pages/EmailConfirmation";
import NotFound from "./pages/NotFound";
import TeamManagement from "./pages/TeamManagement";
import TeamInvitation from "./pages/TeamInvitation";
import Changelog from "./pages/Changelog";

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  console.log('🛡️ ProtectedRoute: Starting render');
  
  const { user, loading } = useAuth();
  const { loading: themeLoading } = useOrganizationTheme();
  const [onboardingComplete, setOnboardingComplete] = React.useState<boolean | null>(null);
  const [userProfile, setUserProfile] = React.useState<any>(null);
  
  // Global background email sync - runs whenever user is logged in
  useBackgroundEmailSync(user?.id);

  console.log('🛡️ ProtectedRoute: State check - user:', !!user, 'loading:', loading, 'themeLoading:', themeLoading, 'onboardingComplete:', onboardingComplete);

  React.useEffect(() => {
    console.log('🔄 ProtectedRoute: useEffect triggered - user:', !!user);
    
    if (!user) {
      console.log('❌ ProtectedRoute: No user, resetting state');
      setOnboardingComplete(null);
      setUserProfile(null);
      return;
    }

    console.log('ProtectedRoute: Checking onboarding for user:', user.id);

    // Check if user completed onboarding and get profile data
    const checkOnboarding = async () => {
      try {
        console.log('🔍 ProtectedRoute: Fetching profile data...');
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed, organization_id, team_id')
          .eq('user_id', user.id)
          .single();
        
        console.log('ProtectedRoute: Profile data fetched:', data, 'Error:', error);
        
        // If there's an auth error (401/403), the session might be stale
        // Don't redirect to onboarding in this case - let auth handle it
        if (error) {
          if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
            console.error('Auth token error, session may be stale:', error);
            // Don't set onboardingComplete to false - keep it null to show loading
            // The auth state listener should handle session refresh or logout
            return;
          }
          
          if (error.code !== 'PGRST116') {
            console.error('Error fetching profile:', error);
            // For other errors, also don't assume onboarding is incomplete
            // Keep showing loading state, retry will happen on next auth state change
            return;
          }
        }
        
        setUserProfile(data);
        // Only set to false if we actually got data and it's explicitly false/null
        // If data is null (no profile found), that's a real new user case
        const isComplete = data?.onboarding_completed === true;
        setOnboardingComplete(isComplete);
        console.log('ProtectedRoute: Onboarding complete set to:', isComplete);
      } catch (err) {
        console.error('Unexpected error in checkOnboarding:', err);
        // On unexpected errors, don't redirect to onboarding
        // Keep showing loading state
      }
    };
    
    checkOnboarding();

    // Set up realtime subscription to listen for profile updates
    const subscription = supabase
      .channel('profile_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('ProtectedRoute: Profile updated via realtime:', payload.new);
          setUserProfile(payload.new);
          setOnboardingComplete(payload.new?.onboarding_completed || false);
          console.log('ProtectedRoute: Onboarding complete updated to:', payload.new?.onboarding_completed || false);
        }
      )
      .subscribe((status) => {
        console.log('ProtectedRoute: Realtime subscription status:', status);
      });

    return () => {
      console.log('ProtectedRoute: Unsubscribing from realtime');
      subscription.unsubscribe();
    };
  }, [user]);

  if (loading || themeLoading || (user && onboardingComplete === null)) {
    console.log('⏳ ProtectedRoute: Showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    console.log('🔒 ProtectedRoute: No user, redirecting to Auth');
    return <Navigate to="/auth" replace />;
  }

  // NEW FLOW: Users join ONLY via team invitation
  // If user has no team and onboarding is incomplete, they need an invitation
  if (user && !onboardingComplete) {
    const currentPath = window.location.pathname;
    // Only allow team-invitation and welcome pages - NO old onboarding
    const allowedPaths = ['/team-invitation', '/welcome'];
    
    console.log('ProtectedRoute: User has incomplete onboarding. Current path:', currentPath);
    
    // Check if user already has a team (edge case: profile not updated yet)
    const hasTeam = userProfile?.team_id != null;
    
     if (hasTeam) {
      console.log('ProtectedRoute: User has team, allowing access');
      // User has team, just needs onboarding_completed to be set - let them through
    } else if (!allowedPaths.some(p => currentPath.startsWith(p))) {
      console.log('ProtectedRoute: User needs team invitation, redirecting');
       let resumeInvitationId: string | null = null;
       try {
         resumeInvitationId = localStorage.getItem('last_team_invitation_id');
       } catch {
         // ignore
       }
      // User has no team and no invitation link - show message
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="w-full max-w-md text-center space-y-6">
            <div className="mx-auto h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Team-Einladung erforderlich</h1>
            <p className="text-muted-foreground">
              Um die App zu nutzen, benötigen Sie eine Einladung von einem Team-Leiter. 
              Bitte wenden Sie sich an Ihren Vorgesetzten, um eine Einladung zu erhalten.
            </p>
             {resumeInvitationId ? (
               <Button
                 onClick={() => {
                   window.location.href = `/team-invitation?id=${encodeURIComponent(resumeInvitationId)}`;
                 }}
               >
                 Zur Einladung
               </Button>
             ) : null}
            <Button 
              variant="outline" 
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = '/auth';
              }}
            >
              Abmelden
            </Button>
          </div>
        </div>
      );
    }
  }

  console.log('✅ ProtectedRoute: Rendering protected content');
  return <>{children}</>;
};

// Version check wrapper component
const VersionCheckWrapper = ({ children }: { children: React.ReactNode }) => {
  const { showReleaseNotes, dismissReleaseNotes, previousVersion } = useVersionCheck();
  
  return (
    <>
      {children}
      <ReleaseNotesDialog 
        open={showReleaseNotes} 
        onClose={dismissReleaseNotes}
        previousVersion={previousVersion}
      />
    </>
  );
};

const App = () => {
  console.log('🎯 App.tsx: App component rendering');
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <VersionCheckWrapper>
          <Toaster />
          <Sonner duration={3000} />
          <BrowserRouter>
            <Routes>
            <Route path="/auth" element={<Auth />} />
            {/* OLD ONBOARDING ROUTES REMOVED - Users join via team invitation only */}
            <Route path="/onboarding" element={<Navigate to="/" replace />} />
            <Route path="/onboarding/processing" element={<Navigate to="/" replace />} />
            <Route path="/onboarding/profile" element={<Navigate to="/" replace />} />
            <Route path="/welcome" element={
              <ProtectedRoute>
                <Welcome />
              </ProtectedRoute>
            } />
            <Route path="/welcome" element={
              <ProtectedRoute>
                <Welcome />
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            } />
            <Route path="/archive" element={
              <ProtectedRoute>
                <Archive />
              </ProtectedRoute>
            } />
            <Route path="/editor" element={
              <ProtectedRoute>
                <ContentEditor />
              </ProtectedRoute>
            } />
            <Route path="/subscription" element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            } />
            <Route path="/running-list" element={
              <ProtectedRoute>
                <RunningList />
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute>
                <CustomerManagement />
              </ProtectedRoute>
            } />
            <Route path="/performance" element={
              <ProtectedRoute>
                <Performance />
              </ProtectedRoute>
            } />
            <Route path="/email" element={
              <ProtectedRoute>
                <Email />
              </ProtectedRoute>
            } />
            <Route path="/email/*" element={
              <ProtectedRoute>
                <Email />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/organization" element={
              <ProtectedRoute>
                <OrganizationManagement />
              </ProtectedRoute>
            } />
            <Route path="/todos" element={
              <ProtectedRoute>
                <TodoManagement />
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <TeamManagement />
              </ProtectedRoute>
            } />
            <Route path="/changelog" element={
              <ProtectedRoute>
                <Changelog />
              </ProtectedRoute>
            } />
            <Route path="/customer-confirmation" element={<CustomerConfirmation />} />
            <Route path="/appointment" element={<CustomerAppointment />} />
            <Route path="/customer-appointment" element={<CustomerAppointment />} />
            <Route path="/email-confirmation" element={<EmailConfirmation />} />
            <Route path="/team-invitation" element={<TeamInvitation />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </VersionCheckWrapper>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
